import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/authContext';
import {
  getSnap,
  recordView,
  toggleReaction,
  addComment,
  getTimeRemaining,
} from '../../api/linguaSnaps';
import './LinguaSnaps.css';
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
  FaRegHeart,
  FaThumbsUp,
  FaLaugh,
  FaSurprise,
  FaSadTear,
  FaAngry,
  FaComment,
  FaPaperPlane,
  FaVolumeMute,
  FaVolumeUp,
  FaPause,
  FaPlay,
  FaEye,
} from 'react-icons/fa';

const LinguaSnapViewer = ({ feed, initialUserIndex = 0, onClose }) => {
  const { user } = useAuth();
  const [userIndex, setUserIndex] = useState(initialUserIndex);
  const [snapIndex, setSnapIndex] = useState(0);
  const [snapDetails, setSnapDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [myReaction, setMyReaction] = useState(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  const viewRecorded = useRef(new Set());

  const REACTIONS = [
    { key: 'like', label: 'Like', Icon: FaThumbsUp },
    { key: 'love', label: 'Love', Icon: FaHeart },
    { key: 'haha', label: 'Haha', Icon: FaLaugh },
    { key: 'wow', label: 'Wow', Icon: FaSurprise },
    { key: 'sad', label: 'Sad', Icon: FaSadTear },
    { key: 'angry', label: 'Angry', Icon: FaAngry },
  ];

  const currentUserData = feed[userIndex];
  const currentSnaps = currentUserData?.snaps || [];
  const currentSnap = currentSnaps[snapIndex];

  // Load snap details
  const loadSnapDetails = useCallback(async (snapId) => {
    if (!snapId) return;
    try {
      setLoading(true);
      const data = await getSnap(snapId);
      setSnapDetails(data);
      setMyReaction(data.myReaction || null);
      setReactionPickerOpen(false);
    } catch (err) {
      console.error('[LinguaSnapViewer] Error loading snap:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentSnap?.id) {
      loadSnapDetails(currentSnap.id);
    }
  }, [currentSnap?.id, loadSnapDetails]);

  // Record view when snap is displayed
  useEffect(() => {
    if (currentSnap?.id && !viewRecorded.current.has(currentSnap.id)) {
      viewRecorded.current.add(currentSnap.id);
      recordView(currentSnap.id).catch(() => {});
    }
  }, [currentSnap?.id]);

  // Video progress tracking
  useEffect(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    if (videoRef.current && !paused) {
      progressInterval.current = setInterval(() => {
        if (videoRef.current) {
          const duration = videoRef.current.duration || 1;
          const currentTime = videoRef.current.currentTime || 0;
          setProgress((currentTime / duration) * 100);
        }
      }, 100);
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [paused, snapIndex, userIndex]);

  // Auto-advance when video ends
  const handleVideoEnd = () => {
    if (snapIndex < currentSnaps.length - 1) {
      setSnapIndex(snapIndex + 1);
      setProgress(0);
    } else if (userIndex < feed.length - 1) {
      setUserIndex(userIndex + 1);
      setSnapIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  };

  // Navigation
  const goToPrevSnap = () => {
    if (snapIndex > 0) {
      setSnapIndex(snapIndex - 1);
      setProgress(0);
    } else if (userIndex > 0) {
      setUserIndex(userIndex - 1);
      const prevUserSnaps = feed[userIndex - 1]?.snaps || [];
      setSnapIndex(prevUserSnaps.length - 1);
      setProgress(0);
    }
  };

  const goToNextSnap = () => {
    if (snapIndex < currentSnaps.length - 1) {
      setSnapIndex(snapIndex + 1);
      setProgress(0);
    } else if (userIndex < feed.length - 1) {
      setUserIndex(userIndex + 1);
      setSnapIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevSnap();
          break;
        case 'ArrowRight':
          goToNextSnap();
          break;
        case ' ':
          e.preventDefault();
          setPaused(!paused);
          break;
        case 'Escape':
          onClose();
          break;
        case 'm':
          setMuted(!muted);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paused, muted, snapIndex, userIndex]);

  // Toggle pause on video click
  const handleVideoClick = () => {
    setPaused(!paused);
  };

  // Update video state when paused changes
  useEffect(() => {
    if (videoRef.current) {
      if (paused) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [paused]);

  const handleReact = async (reaction) => {
    if (!currentSnap?.id || !reaction) return;
    try {
      await toggleReaction(currentSnap.id, reaction);
      await loadSnapDetails(currentSnap.id);
    } catch (err) {
      console.error('[LinguaSnapViewer] Error toggling reaction:', err);
    }
  };

  const currentReactionConfig = REACTIONS.find((r) => r.key === myReaction) || REACTIONS[0];

  // Handle comment submit
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !currentSnap?.id) return;
    try {
      setSubmittingComment(true);
      const { comment } = await addComment(currentSnap.id, commentText.trim());
      setSnapDetails((prev) => ({
        ...prev,
        comments: [...(prev?.comments || []), comment],
      }));
      setCommentText('');
    } catch (err) {
      console.error('[LinguaSnapViewer] Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const snapUser = currentUserData?.user || {};
  const videoUrl = snapDetails?.snap?.videoUrl || currentSnap?.videoUrl;
  const timeLeft = currentSnap ? getTimeRemaining(currentSnap.expires_at) : null;
  const canPrev = snapIndex > 0 || userIndex > 0;
  const canNext = snapIndex < currentSnaps.length - 1 || userIndex < feed.length - 1;

  return (
    <div className="lingua-snap-viewer-overlay" onClick={onClose}>
      <div className="lingua-snap-viewer" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="lingua-snap-close" onClick={onClose}>
          <FaTimes />
        </button>

        {/* Progress bars */}
        <div className="lingua-snap-progress-container">
          {currentSnaps.map((_, idx) => (
            <div key={idx} className="lingua-snap-progress-bar">
              <div
                className="lingua-snap-progress-fill"
                style={{
                  width:
                    idx < snapIndex
                      ? '100%'
                      : idx === snapIndex
                      ? `${progress}%`
                      : '0%',
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="lingua-snap-header">
          <img
            src={snapUser.avatar_url || '/avatar-placeholder.png'}
            alt={snapUser.display_name || snapUser.name || 'User'}
            className="lingua-snap-header-avatar"
          />
          <div className="lingua-snap-header-info">
            <span className="lingua-snap-header-name">
              {snapUser.display_name || snapUser.name || 'User'}
            </span>
            {timeLeft && (
              <span className="lingua-snap-header-time">{timeLeft} left</span>
            )}
          </div>
          <div className="lingua-snap-header-controls">
            <button
              className="lingua-snap-control-btn"
              onClick={() => setPaused(!paused)}
              title={paused ? 'Play' : 'Pause'}
            >
              {paused ? <FaPlay /> : <FaPause />}
            </button>
            <button
              className="lingua-snap-control-btn"
              onClick={() => setMuted(!muted)}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>
          </div>
        </div>

        {/* Video container */}
        <div className="lingua-snap-video-container" onClick={handleVideoClick}>
          {loading && !videoUrl ? (
            <div className="lingua-snap-loading">Loading...</div>
          ) : videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className="lingua-snap-video"
              autoPlay
              muted={muted}
              playsInline
              onEnded={handleVideoEnd}
              onLoadedData={() => {
                if (!paused && videoRef.current) {
                  videoRef.current.play().catch(() => {});
                }
              }}
            />
          ) : (
            <div className="lingua-snap-error">Video unavailable</div>
          )}

          {/* Navigation areas */}
          <div
            className={`lingua-snap-nav-area left ${canPrev ? '' : 'disabled'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (canPrev) goToPrevSnap();
            }}
          >
            <FaChevronLeft />
          </div>
          <div
            className={`lingua-snap-nav-area right ${canNext ? '' : 'disabled'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (canNext) goToNextSnap();
            }}
          >
            <FaChevronRight />
          </div>

          {/* Caption overlay */}
          {currentSnap?.caption && (
            <div className="lingua-snap-caption">{currentSnap.caption}</div>
          )}
        </div>

        {/* Footer with actions */}
        <div className="lingua-snap-footer">
          <div className="lingua-snap-actions">
            <div className="lingua-snap-reaction-group">
              <button
                className={`lingua-snap-action-btn ${myReaction ? 'active' : ''}`}
                onClick={() => setReactionPickerOpen((v) => !v)}
                title={myReaction ? `Reaction: ${myReaction}` : 'React'}
                type="button"
              >
                <currentReactionConfig.Icon />
                {snapDetails?.stats?.reactionCounts && (
                  <span>
                    {Object.values(snapDetails.stats.reactionCounts || {}).reduce((a, b) => a + b, 0) || 0}
                  </span>
                )}
              </button>

              {reactionPickerOpen && (
                <div className="lingua-snap-reaction-picker" onClick={(e) => e.stopPropagation()}>
                  {REACTIONS.map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      className={`lingua-snap-reaction-pick ${myReaction === key ? 'active' : ''}`}
                      onClick={() => handleReact(key)}
                      title={label}
                    >
                      <Icon />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className={`lingua-snap-action-btn ${showComments ? 'active' : ''}`}
              onClick={() => setShowComments(!showComments)}
              title="Comments"
              type="button"
            >
              <FaComment />
              {snapDetails?.comments?.length > 0 && (
                <span>{snapDetails.comments.length}</span>
              )}
            </button>
            <div className="lingua-snap-view-count">
              <FaEye />
              <span>{snapDetails?.stats?.viewCount || 0}</span>
            </div>
          </div>

          {/* Comments section */}
          {showComments && (
            <div className="lingua-snap-comments">
              <div className="lingua-snap-comments-list">
                {(snapDetails?.comments || []).map((comment) => (
                  <div key={comment.id} className="lingua-snap-comment">
                    <img
                      src={comment.profiles?.avatar_url || '/avatar-placeholder.png'}
                      alt=""
                      className="lingua-snap-comment-avatar"
                    />
                    <div className="lingua-snap-comment-content">
                      <span className="lingua-snap-comment-author">
                        {comment.profiles?.display_name || comment.profiles?.name || 'User'}
                      </span>
                      <span className="lingua-snap-comment-text">{comment.body}</span>
                    </div>
                  </div>
                ))}
                {(!snapDetails?.comments || snapDetails.comments.length === 0) && (
                  <div className="lingua-snap-no-comments">No comments yet</div>
                )}
              </div>
              <form className="lingua-snap-comment-form" onSubmit={handleSubmitComment}>
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  disabled={submittingComment}
                />
                <button type="submit" disabled={!commentText.trim() || submittingComment}>
                  <FaPaperPlane />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinguaSnapViewer;
